import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gauge, AlertTriangle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

export const useCredits = (deps = []) => {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/admin-ai/crediti").then((r) => setC(r.data)).catch(() => {}); }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return [c, setC];
};

const num = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
const mese = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString("it-IT", { month: "long", year: "numeric" });

export const CreditsBar = ({ c }) => {
  if (!c) return null;
  const pct = Math.min(100, Math.round((c.usati / Math.max(1, c.limite)) * 100));
  const full = c.usati >= c.limite;
  const warn = pct >= 80;
  return (
    <div className="rounded-xl border border-[#5FB3EA] bg-white/80 px-3 py-2" data-testid="tavily-credits">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-bold text-[#0A1128]">
        <span className="inline-flex items-center gap-1.5"><Gauge className="h-4 w-4 text-[#E8173A]" />Ricerca web: <span data-testid="tavily-credits-value">{num(c.usati)} / {num(c.limite)}</span> crediti gratuiti ({mese(c.mese)})</span>
        <span className="text-slate-500 font-semibold">{c.analisi} analisi</span>
      </div>
      <div className="mt-1.5 h-2 rounded-full bg-slate-200 overflow-hidden"><div className={`h-full rounded-full ${full ? "bg-[#E8173A]" : warn ? "bg-amber-500" : "bg-emerald-600"}`} style={{ width: `${pct}%` }} /></div>
      {warn && (
        <div className={`mt-1.5 flex gap-1.5 text-xs font-semibold ${full ? "text-[#E8173A]" : "text-amber-800"}`} data-testid={full ? "tavily-credits-full" : "tavily-credits-warning"}>
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          {full ? `Crediti del mese esauriti${c.blocco ? " (blocco attivo)" : ""}: usa la modalità «stima» (solo AI, nessun credito) fino al prossimo mese.` : `Hai usato l'${pct}% dei crediti del mese.`}
        </div>
      )}
    </div>
  );
};

export const CreditsSettings = () => {
  const [c, setC] = useCredits();
  const [f, setF] = useState(null);
  useEffect(() => { if (c && !f) setF({ limite: c.limite, blocco: c.blocco }); }, [c, f]);
  const save = async () => {
    try { setC((await api.put("/admin-ai/crediti", { limite: Number(f.limite), blocco: f.blocco })).data); toast.success("Limite crediti salvato"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="tavily-credits-settings">
      <CreditsBar c={c} />
      {f && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs font-bold text-slate-600">Limite mensile crediti
            <Input type="number" min={1} value={f.limite} onChange={(e) => setF({ ...f, limite: e.target.value })} className="mt-1 w-[140px] rounded-xl bg-white" data-testid="tavily-limit-input" /></label>
          <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A1128] pb-2">
            <Switch checked={f.blocco} onCheckedChange={(v) => setF({ ...f, blocco: v })} className="data-[state=checked]:bg-[#E8173A]" data-testid="tavily-block-switch" />Blocca la ricerca web al raggiungimento del limite</label>
          <Button onClick={save} className="rounded-full bg-[#0A1128] font-bold" data-testid="tavily-limit-save">Salva</Button>
        </div>
      )}
    </div>
  );
};
